import { useState, useEffect, useCallback } from 'react';
import { getUserProfile } from '../../../services/userService';
import { User } from '../../../types/user';
import { ERROR_MESSAGES } from '../constants';

/**
 * Hook para gerenciar dados do perfil do usuário
 *
 * @returns Estado e funções para gerenciar perfil
 *
 * @example
 * ```tsx
 * const { user, loading, error, refetch } = useUserProfile();
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage>{error}</ErrorMessage>;
 *
 * return <div>{user?.name}</div>;
 * ```
 */
export const useUserProfile = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getUserProfile();

      if (response.success) {
        setUser(response.data);
      } else {
        setError(response.message || ERROR_MESSAGES.fetchProfile);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : ERROR_MESSAGES.fetchProfile;
      setError(errorMessage);
      console.error('Error fetching user profile:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  return {
    user,
    loading,
    error,
    refetch: fetchUserProfile
  };
};
